import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import CreativePublicationProofViewer
  from "./CreativePublicationProofViewer";

import {
  ReviewMatterLayoutPreview,
  TextbookPublicationProofChapterPages,
  reviewBookMatter,
  reviewChapters,
  textbookReviewLayoutPreview,
} from "./TextbookBookReview";

function clean(value) {
  return String(
    value ?? ""
  ).trim();
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function safeObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  )
    ? value
    : {};
}

function matterHasWriting(
  item
) {
  return safeArray(
    item
      ?.manuscriptContent
      ?.blocks
  ).some(
    (block) =>
      safeArray(
        block?.runs
      ).some(
        (run) =>
          clean(run?.text)
      )
  );
}

function activeAsset(
  assets,
  purpose
) {
  return (
    safeArray(assets)
      .find(
        (asset) =>
          clean(
            asset?.purpose
          ) === purpose &&
          clean(
            asset?.status
          ) === "active"
      ) ||
    null
  );
}

function chapterResultReady(
  result
) {
  if (
    !result ||
    result.loading ||
    clean(result.error)
  ) {
    return false;
  }

  return safeArray(
    result
      ?.chapterDraft
      ?.blocks
  ).some(
    (block) => {
      if (
        clean(block?.type) ===
        "figure"
      ) {
        return true;
      }

      if (
        clean(block?.text)
      ) {
        return true;
      }

      return safeArray(
        block
          ?.manuscriptContent
          ?.blocks
      ).some(
        (richBlock) =>
          safeArray(
            richBlock?.runs
          ).some(
            (run) =>
              clean(
                run?.text
              )
          )
      );
    }
  );
}

export default function TextbookPublicationProof({
  project,
  projectPlan,
  onLoadChapterDraft,
  onLoadFigureAssets,
  onLoadFigureFile,
  onLoadPublicationAssets,
  onLoadPublicationAssetFile,
  onOpenChapter,
  onOpenBookStructure,
  onOpenPublicationPreparation,
  onReturn,
  isFR = false,
}) {
  const projectId =
    clean(project?.id);

  const frontMatter =
    useMemo(
      () =>
        reviewBookMatter(
          projectPlan
            ?.bookMap,
          "front_matter"
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const chapters =
    useMemo(
      () =>
        reviewChapters(
          projectPlan
            ?.bookMap
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const backMatter =
    useMemo(
      () =>
        reviewBookMatter(
          projectPlan
            ?.bookMap,
          "back_matter"
        ),
      [
        projectPlan
          ?.bookMap,
      ]
    );

  const [
    chapterResults,
    setChapterResults,
  ] = useState({});

  const [
    chapterLoading,
    setChapterLoading,
  ] = useState(false);

  const [
    publicationAssets,
    setPublicationAssets,
  ] = useState([]);

  const [
    coverPreviewUrls,
    setCoverPreviewUrls,
  ] = useState({});

  const [
    coverPreviewErrors,
    setCoverPreviewErrors,
  ] = useState({});

  useEffect(() => {
    let cancelled = false;

    if (
      !projectId ||
      chapters.length === 0 ||
      typeof onLoadChapterDraft !==
        "function"
    ) {
      setChapterResults({});
      setChapterLoading(false);

      return () => {
        cancelled = true;
      };
    }

    setChapterLoading(true);

    (
      async () => {
        const next = {};
        let cursor = 0;

        async function worker() {
          while (
            !cancelled &&
            cursor < chapters.length
          ) {
            const chapter =
              chapters[cursor];

            cursor += 1;

            try {
              const payload =
                await onLoadChapterDraft(
                  projectId,
                  chapter.itemId
                );

              let figureAssets = [];
              let figureError = "";

              if (
                typeof onLoadFigureAssets ===
                  "function"
              ) {
                try {
                  const figurePayload =
                    await onLoadFigureAssets(
                      projectId,
                      chapter.itemId
                    );

                  figureAssets =
                    safeArray(
                      figurePayload
                        ?.figureAssets
                    );
                } catch (
                  figureLoadError
                ) {
                  figureError =
                    clean(
                      figureLoadError
                        ?.message
                    ) ||
                    "Figure load failed.";
                }
              }

              next[
                chapter.itemId
              ] = {
                loading: false,
                error: "",
                chapter:
                  payload
                    ?.chapter ||
                  null,
                chapterDraft:
                  payload
                    ?.chapterDraft ||
                  null,
                figureAssets,
                figureError,
              };
            } catch (error) {
              next[
                chapter.itemId
              ] = {
                loading: false,
                error:
                  clean(
                    error
                      ?.message
                  ) ||
                  (
                    isFR
                      ? "Impossible de charger ce chapitre."
                      : "Could not load this Chapter."
                  ),
                chapterDraft:
                  null,
                figureAssets: [],
                figureError: "",
              };
            }
          }
        }

        await Promise.all(
          Array.from(
            {
              length:
                Math.max(
                  1,
                  Math.min(
                    4,
                    chapters.length
                  )
                ),
            },
            () =>
              worker()
          )
        );

        if (cancelled) {
          return;
        }

        setChapterResults(next);
        setChapterLoading(false);
      }
    )();

    return () => {
      cancelled = true;
    };
  }, [
    chapters,
    isFR,
    onLoadChapterDraft,
    onLoadFigureAssets,
    projectId,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (
      !projectId ||
      typeof onLoadPublicationAssets !==
        "function"
    ) {
      setPublicationAssets([]);

      return () => {
        cancelled = true;
      };
    }

    (
      async () => {
        try {
          const payload =
            await onLoadPublicationAssets(
              projectId
            );

          if (cancelled) {
            return;
          }

          setPublicationAssets(
            safeArray(
              payload
                ?.publicationAssets
            )
          );
        } catch {
          if (!cancelled) {
            setPublicationAssets([]);
          }
        }
      }
    )();

    return () => {
      cancelled = true;
    };
  }, [
    onLoadPublicationAssets,
    projectId,
  ]);

  const frontAsset =
    activeAsset(
      publicationAssets,
      "front_cover"
    );

  const backAsset =
    activeAsset(
      publicationAssets,
      "back_cover"
    );

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    setCoverPreviewUrls({});
    setCoverPreviewErrors({});

    const assets = [
      [
        "front",
        frontAsset,
      ],
      [
        "back",
        backAsset,
      ],
    ];

    if (
      typeof onLoadPublicationAssetFile !==
        "function"
    ) {
      return () => {
        cancelled = true;
      };
    }

    (
      async () => {
        const urls = {};
        const errors = {};

        for (
          const [
            key,
            asset,
          ] of assets
        ) {
          const publicUrl =
            clean(
              asset?.publicUrl
            );

          if (!publicUrl) {
            continue;
          }

          try {
            const blob =
              await onLoadPublicationAssetFile(
                publicUrl
              );

            if (
              cancelled ||
              typeof URL ===
                "undefined" ||
              typeof URL
                .createObjectURL !==
                "function"
            ) {
              continue;
            }

            const objectUrl =
              URL.createObjectURL(
                blob
              );

            objectUrls.push(
              objectUrl
            );

            urls[key] =
              objectUrl;
          } catch {
            errors[key] =
              isFR
                ? "Impossible de charger cette couverture enregistrée."
                : "Could not load this saved cover.";
          }
        }

        if (!cancelled) {
          setCoverPreviewUrls(
            urls
          );

          setCoverPreviewErrors(
            errors
          );
        }
      }
    )();

    return () => {
      cancelled = true;

      if (
        typeof URL !==
          "undefined" &&
        typeof URL
          .revokeObjectURL ===
          "function"
      ) {
        objectUrls.forEach(
          (url) =>
            URL
              .revokeObjectURL(
                url
              )
        );
      }
    };
  }, [
    backAsset,
    frontAsset,
    isFR,
    onLoadPublicationAssetFile,
  ]);

  const layout =
    textbookReviewLayoutPreview(
      projectPlan
        ?.formatPlan ||
      {}
    );

  const format = {
    trim: {
      measurable:
        layout.ready ===
        true,

      label:
        layout.label ||
        (
          isFR
            ? "Format non sélectionné"
            : "Book format not selected"
        ),

      width:
        Number(
          layout.widthInches ||
          0
        ),

      height:
        Number(
          layout.heightInches ||
          0
        ),
    },

    bleed:
      clean(
        projectPlan
          ?.formatPlan
          ?.bleed
      ),
  };

  const interiorItems =
    useMemo(
      () => [
        ...frontMatter.map(
          (item, index) => ({
            ...item,
            proofKind:
              "front_matter",
            pageLabel:
              isFR
                ? `Pages liminaires ${index + 1} sur ${frontMatter.length} · ${
                    clean(item?.title) ||
                    "Sans titre"
                  }`
                : `Front Matter ${index + 1} of ${frontMatter.length} · ${
                    clean(item?.title) ||
                    "Untitled"
                  }`,
            ready:
              matterHasWriting(
                item
              ),
          })
        ),

        ...chapters.map(
          (chapter) => ({
            ...chapter,
            proofKind:
              "chapter",
            pageLabel:
              isFR
                ? `Chapitre ${chapter.chapterNumber} sur ${chapters.length} · ${
                    clean(
                      chapter
                        ?.title
                    ) ||
                    "Sans titre"
                  }`
                : `Chapter ${chapter.chapterNumber} of ${chapters.length} · ${
                    clean(
                      chapter
                        ?.title
                    ) ||
                    "Untitled"
                  }`,
            ready:
              chapterResultReady(
                chapterResults[
                  chapter.itemId
                ]
              ),
          })
        ),

        ...backMatter.map(
          (item, index) => ({
            ...item,
            proofKind:
              "back_matter",
            pageLabel:
              isFR
                ? `Élément de fin ${index + 1} sur ${backMatter.length} · ${
                    clean(item?.title) ||
                    "Sans titre"
                  }`
                : `Back Matter ${index + 1} of ${backMatter.length} · ${
                    clean(item?.title) ||
                    "Untitled"
                  }`,
            ready:
              matterHasWriting(
                item
              ),
          })
        ),
      ],
      [
        backMatter,
        chapterResults,
        chapters,
        frontMatter,
        isFR,
      ]
    );

  const issues = [];

  if (!layout.ready) {
    issues.push({
      severity:
        "blocking",
      code:
        "textbook_proof_trim_missing",
    });
  }

  if (
    chapters.length < 1
  ) {
    issues.push({
      severity:
        "blocking",
      code:
        "textbook_proof_chapter_missing",
    });
  }

  if (!frontAsset) {
    issues.push({
      severity:
        "warning",
      code:
        "textbook_proof_front_cover_missing",
    });
  }

  if (!backAsset) {
    issues.push({
      severity:
        "warning",
      code:
        "textbook_proof_back_cover_missing",
    });
  }

  function issueText(
    item
  ) {
    const code =
      clean(item?.code);

    const messages = {
      textbook_proof_trim_missing:
        isFR
          ? "Sélectionnez le format du livre pour afficher l’épreuve physique."
          : "Select the book format before relying on the physical proof.",

      textbook_proof_chapter_missing:
        isFR
          ? "Ajoutez au moins un chapitre avant l’épreuve de publication."
          : "Add at least one Chapter before Publication Proof.",

      textbook_proof_front_cover_missing:
        isFR
          ? "Aucune image de première de couverture n’est enregistrée ; l’épreuve utilise un substitut."
          : "No saved front-cover image is available; the proof uses a placeholder.",

      textbook_proof_back_cover_missing:
        isFR
          ? "Aucune image de quatrième de couverture n’est enregistrée ; l’épreuve utilise le contenu enregistré."
          : "No saved back-cover image is available; the proof uses the saved back-cover content.",
    };

    return (
      messages[code] ||
      code
    );
  }

  const publicationPlan =
    safeObject(
      projectPlan
        ?.publicationPlan
    );

  const frontPlan =
    safeObject(
      publicationPlan
        ?.frontCover
    );

  const backPlan =
    safeObject(
      publicationPlan
        ?.backCover
    );

  const title =
    clean(
      frontPlan?.title
    ) ||
    clean(project?.title) ||
    (
      isFR
        ? "Manuel sans titre"
        : "Untitled Textbook"
    );

  const subtitle =
    clean(
      frontPlan?.subtitle
    );

  const author =
    clean(
      frontPlan?.authorName
    );

  function coverPanel({
    side,
    imageUrl,
    previewError,
    children,
  }) {
    return (
      <section
        aria-label={
          side === "front"
            ? (
                isFR
                  ? "Première de couverture"
                  : "Front cover"
              )
            : (
                isFR
                  ? "Quatrième de couverture"
                  : "Back cover"
              )
        }
        style={{
          position:
            "relative",
          aspectRatio:
            layout.ready
              ? `${
                  layout.widthInches
                } / ${
                  layout.heightInches
                }`
              : "4 / 5",
          minHeight: 340,
          borderRadius: 12,
          border:
            "1px solid #cbd5e1",
          background:
            "#f8fafc",
          overflow:
            "hidden",
          boxSizing:
            "border-box",
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={
              side === "front"
                ? (
                    isFR
                      ? "Première de couverture enregistrée"
                      : "Saved front cover"
                  )
                : (
                    isFR
                      ? "Quatrième de couverture enregistrée"
                      : "Saved back cover"
                  )
            }
            style={{
              width: "100%",
              height: "100%",
              objectFit:
                "contain",
              background:
                "#ffffff",
              display:
                "block",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              padding: 26,
              boxSizing:
                "border-box",
              display: "flex",
              flexDirection:
                "column",
              justifyContent:
                "center",
              gap: 12,
              background:
                side === "front"
                  ? "linear-gradient(145deg, #ecfdf5, #f8fafc)"
                  : "linear-gradient(145deg, #fff7ed, #f8fafc)",
            }}
          >
            {children}
          </div>
        )}

        {previewError ? (
          <div
            style={{
              position:
                "absolute",
              left: 10,
              right: 10,
              bottom: 10,
              padding:
                "7px 9px",
              borderRadius:
                8,
              background:
                "rgba(255,255,255,0.94)",
              color:
                "#991b1b",
              fontSize: 11,
              fontWeight:
                800,
            }}
          >
            {previewError}
          </div>
        ) : null}
      </section>
    );
  }

  function renderCover() {
    return (
      <div
        data-textbook-publication-cover-proof="true"
        style={{
          overflowX:
            "auto",
        }}
      >
        <div
          style={{
            marginBottom: 12,
            fontSize: 13,
            fontWeight: 800,
            color: "#475569",
            textAlign:
              "center",
          }}
        >
          {isFR
            ? "Quatrième de couverture · Dos · Première de couverture"
            : "Back cover · Spine · Front cover"}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(260px, 1fr) minmax(58px, 0.16fr) minmax(260px, 1fr)",
            minWidth: 650,
            gap: 8,
            alignItems:
              "stretch",
          }}
        >
          {coverPanel({
            side:
              "back",
            imageUrl:
              coverPreviewUrls
                .back,
            previewError:
              coverPreviewErrors
                .back,
            children: (
              <>
                <h3
                  style={{
                    margin: 0,
                    textAlign:
                      "center",
                  }}
                >
                  {title}
                </h3>

                <p
                  style={{
                    margin: 0,
                    whiteSpace:
                      "pre-wrap",
                    lineHeight: 1.55,
                  }}
                >
                  {clean(
                    backPlan?.blurb
                  ) ||
                    (
                      isFR
                        ? "Ajoutez le texte de quatrième de couverture dans Préparation de publication."
                        : "Add the back-cover description in Publication Preparation."
                    )}
                </p>

                {clean(
                  backPlan
                    ?.authorBio
                ) ? (
                  <p
                    style={{
                      margin:
                        "auto 0 0",
                      fontSize: 12,
                      lineHeight:
                        1.45,
                    }}
                  >
                    {
                      backPlan
                        .authorBio
                    }
                  </p>
                ) : null}
              </>
            ),
          })}

          <section
            aria-label={
              isFR
                ? "Dos provisoire"
                : "Provisional spine"
            }
            style={{
              minHeight: 340,
              borderRadius:
                10,
              border:
                "1px solid #cbd5e1",
              background:
                "#ecfdf5",
              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "center",
              padding:
                "18px 8px",
              boxSizing:
                "border-box",
            }}
          >
            <strong
              style={{
                writingMode:
                  "vertical-rl",
                textOrientation:
                  "mixed",
                textAlign:
                  "center",
                color:
                  "#065f46",
                letterSpacing:
                  "0.04em",
              }}
            >
              {title}
            </strong>
          </section>

          {coverPanel({
            side:
              "front",
            imageUrl:
              coverPreviewUrls
                .front,
            previewError:
              coverPreviewErrors
                .front,
            children: (
              <>
                <h2
                  style={{
                    margin: 0,
                    fontSize:
                      "clamp(26px, 4vw, 42px)",
                    lineHeight:
                      1.08,
                  }}
                >
                  {title}
                </h2>

                {subtitle ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 18,
                    }}
                  >
                    {subtitle}
                  </p>
                ) : null}

                {author ? (
                  <p
                    style={{
                      margin:
                        "auto 0 0",
                      fontWeight:
                        800,
                    }}
                  >
                    {author}
                  </p>
                ) : null}
              </>
            ),
          })}
        </div>

        <div
          style={{
            marginTop: 12,
            color:
              "#9a3412",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Le dos est provisoire dans cette épreuve visuelle. Sa largeur finale sera calculée après l’approbation de l’intérieur et le verrouillage du nombre final de pages."
            : "The spine is provisional in this visual proof. Final spine width is calculated after Interior Approval locks the final page count."}
        </div>

        {typeof onOpenPublicationPreparation ===
          "function" ? (
          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={
              onOpenPublicationPreparation
            }
            style={{
              marginTop: 14,
            }}
          >
            {isFR
              ? "Modifier la préparation de couverture"
              : "Edit Cover Preparation"}
          </button>
        ) : null}
      </div>
    );
  }

  function renderInteriorItem(
    item
  ) {
    const kind =
      clean(
        item?.proofKind
      );

    const sourceAction =
      kind === "chapter"
        ? (
            typeof onOpenChapter ===
              "function" ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onOpenChapter(
                    item.itemId
                  )
                }
              >
                {isFR
                  ? "Modifier le chapitre source"
                  : "Edit Source Chapter"}
              </button>
            ) : null
          )
        : (
            typeof onOpenBookStructure ===
              "function" ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={
                  onOpenBookStructure
                }
              >
                {isFR
                  ? "Modifier la source dans Structure du livre"
                  : "Edit Source in Book Structure"}
              </button>
            ) : null
          );

    return (
      <div
        data-textbook-publication-interior-proof-item={
          kind
        }
      >
        {sourceAction ? (
          <div
            style={{
              display: "flex",
              justifyContent:
                "flex-end",
              marginBottom:
                10,
            }}
          >
            {sourceAction}
          </div>
        ) : null}

        {kind === "chapter" ? (
          <TextbookPublicationProofChapterPages
            item={item}
            result={
              chapterResults[
                item.itemId
              ] || {
                loading:
                  chapterLoading,
              }
            }
            formatPlan={
              projectPlan
                ?.formatPlan ||
              {}
            }
            onLoadFigureFile={
              onLoadFigureFile
            }
            isFR={isFR}
          />
        ) : (
          <ReviewMatterLayoutPreview
            item={{
              ...item,

              kind:
                kind ===
                  "front_matter"
                  ? "front"
                  : "back",
            }}
            formatPlan={
              projectPlan
                ?.formatPlan ||
              {}
            }
            isFR={isFR}
          />
        )}
      </div>
    );
  }

  return (
    <section
      data-textbook-publication-proof="nbb-v1"
    >
      <CreativePublicationProofViewer
        lang={
          isFR
            ? "fr"
            : "en"
        }
        title={
          clean(
            project?.title
          ) ||
          (
            isFR
              ? "Épreuve de publication du manuel"
              : "Textbook Publication Proof"
          )
        }
        format={format}
        cover={{
          frontAsset,
          backAsset,
        }}
        interiorItems={
          interiorItems
        }
        issues={issues}
        renderCover={
          renderCover
        }
        renderInteriorItem={
          renderInteriorItem
        }
        formatIssue={
          issueText
        }
        onReturn={
          onReturn
        }
        returnLabel={
          isFR
            ? "Retour à Réviser votre livre"
            : "Back to Review Your Book"
        }
        coverContentGeometry
        directInteriorNavigation
        stickyInteriorNavigation
      />
    </section>
  );
}
